import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, RefreshCw } from "lucide-react";
import { formatApiError } from "../../lib/api";
import {
  getServiceSparePartOptions,
  normalizeServiceSelectorResponse,
} from "../../lib/serviceApi";
import { Button } from "../ui/button";
import UnifiedUsedPartPanel from "./UnifiedUsedPartPanel";

function holdId(option) {
  return String(option?.hold_id || option?.id || "").trim();
}

function usageId(option) {
  return String(option?.usage_id || (option?.record_type === "usage" ? option?.id : "") || "").trim();
}

function previewOrigin(option) {
  if (option?.preview_origin === "hold" || option?.preview_origin === "usage") return option.preview_origin;
  return option?.record_type === "usage" || usageId(option) ? "usage" : "hold";
}

export function standardMovePreviewId(option) {
  if (option?.preview_id) return option.preview_id;
  const origin = previewOrigin(option);
  const id = origin === "usage" ? usageId(option) : holdId(option);
  return id ? `${origin}:${id}` : "";
}

function withPreviewIdentity(option, origin, index, seen) {
  const id = origin === "usage" ? usageId(option) : holdId(option);
  const baseId = id ? `${origin}:${id}` : `${origin}:invalid`;
  const occurrence = seen.get(baseId) || 0;
  seen.set(baseId, occurrence + 1);
  const previewId = occurrence === 0 ? baseId : `${baseId}:duplicate:${index}`;
  return { ...option, preview_origin: origin, preview_id: previewId };
}

function uniquePreviewRows(rows) {
  const seen = new Set();
  return (rows || []).filter((row) => {
    const id = standardMovePreviewId(row);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function validateStandardMovePreviewIds(values, fieldName) {
  const normalized = (values || []).map((value) => String(value || "").trim());
  if (normalized.some((value) => !value)) return { ids: [], error: `${fieldName} contains a blank ID.` };
  if (new Set(normalized).size !== normalized.length) {
    return { ids: [], error: `${fieldName} contains duplicate IDs.` };
  }
  return { ids: normalized.sort(), error: "" };
}

export function buildStandardMoveSparePartPreview(selector = {}) {
  const holdIds = new Map();
  const usageIds = new Map();
  return {
    holdRows: uniquePreviewRows(
      (selector.hold_side || []).map((row, index) => withPreviewIdentity(row, "hold", index, holdIds))
    ),
    usedRows: uniquePreviewRows(
      (selector.used_side || [])
        .filter((row) => row?.source_type !== "cannibal")
        .map((row, index) => withPreviewIdentity(row, "usage", index, usageIds))
    ),
  };
}

export function moveStandardMoveSparePartRows(preview, direction, selectedIds = []) {
  const selected = new Set(selectedIds);
  const holdRows = uniquePreviewRows(preview?.holdRows || []);
  const usedRows = uniquePreviewRows(preview?.usedRows || []);
  if (direction === "to_used") {
    const moved = holdRows.filter((row) => selected.has(standardMovePreviewId(row)));
    return {
      holdRows: holdRows.filter((row) => !selected.has(standardMovePreviewId(row))),
      usedRows: uniquePreviewRows([...usedRows, ...moved]),
    };
  }
  if (direction === "to_hold") {
    const moved = usedRows.filter((row) => selected.has(standardMovePreviewId(row)));
    return {
      holdRows: uniquePreviewRows([...holdRows, ...moved]),
      usedRows: usedRows.filter((row) => !selected.has(standardMovePreviewId(row))),
    };
  }
  return { holdRows, usedRows };
}

export function deriveStandardMoveFinalUsedState(usedRows = []) {
  const holdResult = validateStandardMovePreviewIds(
    usedRows.filter((row) => previewOrigin(row) === "hold").map(holdId),
    "final_used_hold_ids"
  );
  const usageResult = validateStandardMovePreviewIds(
    usedRows.filter((row) => previewOrigin(row) === "usage").map(usageId),
    "final_active_usage_ids"
  );
  return {
    finalUsedHoldIds: holdResult.ids,
    finalActiveUsageIds: usageResult.ids,
    validationError: [holdResult.error, usageResult.error].filter(Boolean).join(" "),
  };
}

export function isSameStandardMoveFinalUsedState(initialState, finalState) {
  if (initialState?.validationError || finalState?.validationError) return false;
  return (
    JSON.stringify(initialState?.finalUsedHoldIds || []) === JSON.stringify(finalState?.finalUsedHoldIds || []) &&
    JSON.stringify(initialState?.finalActiveUsageIds || []) === JSON.stringify(finalState?.finalActiveUsageIds || [])
  );
}

export function shouldPreviewStandardMoveAutomaticReversal(savedRepairAction, finalRepairAction) {
  return savedRepairAction === "part_replace" && ["part_repair", "software_repair", "cancel_service"].includes(finalRepairAction);
}

export function buildStandardMoveAutomaticReversalPreview(initialPreview) {
  return {
    holdRows: uniquePreviewRows([...(initialPreview?.holdRows || []), ...(initialPreview?.usedRows || [])]),
    usedRows: [],
  };
}

function isSelectable(option) {
  if (option?.selectable === false || option?.disabled === true) return false;
  if (previewOrigin(option) === "usage") {
    return Boolean(
      usageId(option) &&
        option?.record_type === "usage" &&
        option?.is_current_ticket_relationship === true &&
        (option?.usage_status === "active" || option?.usage_status === null || option?.usage_status === undefined)
    );
  }
  return Boolean(holdId(option) && option?.record_type === "hold" && option?.read_only !== true);
}

const transferColumns = (readOnly) => readOnly
  ? "grid-cols-[minmax(120px,0.75fr)_minmax(200px,1.25fr)]"
  : "grid-cols-[44px_minmax(120px,0.75fr)_minmax(200px,1.25fr)]";

function TransferRow({ option, checked, disabled, onToggle, readOnly = false }) {
  return (
    <label
      data-preview-id={standardMovePreviewId(option)}
      className={`grid ${transferColumns(readOnly)} items-start border-b border-zinc-100 px-3 py-3 text-sm last:border-b-0 dark:border-zinc-800 ${
        disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900"
      }`}
    >
      {!readOnly ? (
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onToggle(event.target.checked)}
          className="h-5 w-5 rounded-sm border-zinc-300 text-primary focus:ring-primary disabled:cursor-not-allowed"
        />
      ) : null}
      <div className="text-xs font-semibold tracking-[0.02em] text-zinc-700 dark:text-zinc-300" data-ledger-column="model">
        {option?.model || "-"}
      </div>
      <div className="min-w-0" data-ledger-column="hold-part">
        <div className="font-medium text-zinc-900 dark:text-zinc-100">
          {option?.part_name || "Unnamed spare part"}
        </div>
        <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
          {option?.part_number || "-"}
        </div>
      </div>
    </label>
  );
}

function TransferPanel({ title, rows, selectedIds, onToggle, onAction, actionLabel, actionDisabled, used, stateMessage, readOnly = false }) {
  return (
    <section data-transfer-panel={used ? "used" : "hold"} className="min-h-[230px] overflow-hidden rounded-sm border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
      <div className={`grid ${transferColumns(readOnly)} items-center border-b border-zinc-200 bg-zinc-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400`} data-testid="standard-move-hold-header">
        {!readOnly ? <div /> : null}
        <div data-ledger-column="model">MODEL</div>
        <div className="flex items-center justify-between gap-3" data-ledger-column="hold-part">
          <span>{title}</span>
          {!readOnly ? (
            <Button
              type="button"
              onClick={onAction}
              disabled={actionDisabled}
              variant={used ? "transfer-remove" : "transfer-add"}
              size="sm"
              data-action-category={used ? "transfer-remove" : "transfer-add"}
            >
              {used ? <ArrowLeft className="h-3.5 w-3.5" /> : null}
              {actionLabel}
              {!used ? <ArrowRight className="h-3.5 w-3.5" /> : null}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="max-h-[270px] overflow-y-auto">
        {stateMessage ? (
          <div className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">{stateMessage}</div>
        ) : rows.length > 0 ? (
          rows.map((row) => {
            const id = standardMovePreviewId(row);
            const disabled = !onToggle || !isSelectable(row);
            return (
              <TransferRow
                key={id}
                option={row}
                checked={selectedIds.includes(id)}
                disabled={disabled}
                onToggle={() => onToggle?.(id)}
                readOnly={readOnly}
              />
            );
          })
        ) : (
          <div className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
            No {title.toLowerCase()} records.
          </div>
        )}
      </div>
    </section>
  );
}

const EMPTY_PREVIEW = { holdRows: [], usedRows: [] };

export default function SparePartSelector({
  ticketId,
  active,
  repairAction,
  savedRepairAction,
  disabled = false,
  fieldError = "",
  onStateChange,
  cannibalLedger = null,
}) {
  const [initialPreview, setInitialPreview] = useState(EMPTY_PREVIEW);
  const [holdRows, setHoldRows] = useState([]);
  const [usedRows, setUsedRows] = useState([]);
  const [selectedHoldIds, setSelectedHoldIds] = useState([]);
  const [selectedUsedIds, setSelectedUsedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const requestIdRef = useRef(0);

  const automaticReversal = shouldPreviewStandardMoveAutomaticReversal(
    savedRepairAction,
    repairAction
  );
  const manualEnabled = active && loaded && !error && repairAction === "part_replace" && !disabled;

  const loadOptions = useCallback(async () => {
    if (!ticketId || !active) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setLoaded(false);
    setError("");
    try {
      const response = await getServiceSparePartOptions(ticketId);
      if (requestIdRef.current !== requestId) return;
      const preview = buildStandardMoveSparePartPreview(normalizeServiceSelectorResponse(response));
      setInitialPreview(preview);
      setHoldRows(preview.holdRows);
      setUsedRows(preview.usedRows);
      setSelectedHoldIds([]);
      setSelectedUsedIds([]);
      setLoaded(true);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setInitialPreview(EMPTY_PREVIEW);
      setHoldRows([]);
      setUsedRows([]);
      setSelectedHoldIds([]);
      setSelectedUsedIds([]);
      setLoaded(false);
      setError(formatApiError(err));
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [active, ticketId]);

  useEffect(() => {
    if (!active) {
      requestIdRef.current += 1;
      setInitialPreview(EMPTY_PREVIEW);
      setHoldRows([]);
      setUsedRows([]);
      setSelectedHoldIds([]);
      setSelectedUsedIds([]);
      setLoading(false);
      setError("");
      setLoaded(false);
      return;
    }
    loadOptions();
  }, [active, loadOptions]);

  const initialFinalState = useMemo(
    () => deriveStandardMoveFinalUsedState(initialPreview.usedRows),
    [initialPreview]
  );
  const finalState = useMemo(() => deriveStandardMoveFinalUsedState(usedRows), [usedRows]);
  const validationError = finalState.validationError || initialFinalState.validationError;
  const changed = Boolean(
    loaded &&
      repairAction === "part_replace" &&
      !automaticReversal &&
      !validationError &&
      !isSameStandardMoveFinalUsedState(initialFinalState, finalState)
  );

  const holdsToConvert = useMemo(
    () => usedRows.filter((row) => previewOrigin(row) === "hold"),
    [usedRows]
  );
  const finalUsedIds = useMemo(() => new Set(usedRows.map(standardMovePreviewId)), [usedRows]);
  const usagesToReturn = useMemo(
    () => automaticReversal
      ? initialPreview.usedRows
      : initialPreview.usedRows.filter((row) => !finalUsedIds.has(standardMovePreviewId(row))),
    [automaticReversal, finalUsedIds, initialPreview.usedRows]
  );

  useEffect(() => {
    onStateChange?.({
      changed,
      finalUsedHoldIds: finalState.finalUsedHoldIds,
      finalActiveUsageIds: finalState.finalActiveUsageIds,
      loading,
      error,
      loaded,
      validationError,
      automaticReversal,
      summary: { holdsToConvert, usagesToReturn },
    });
  }, [automaticReversal, changed, error, finalState, holdsToConvert, loaded, loading, onStateChange, usagesToReturn, validationError]);

  const toggleSelection = (setter, id) => {
    setter((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const moveSelected = (direction) => {
    const selectedIds = direction === "to_used" ? selectedHoldIds : selectedUsedIds;
    const preview = moveStandardMoveSparePartRows({ holdRows, usedRows }, direction, selectedIds);
    setHoldRows(preview.holdRows);
    setUsedRows(preview.usedRows);
    setSelectedHoldIds([]);
    setSelectedUsedIds([]);
  };

  const stateMessage = loading && !loaded
    ? "Loading spare-part options..."
    : error
      ? "Spare-part options are unavailable."
      : "";

  return (
    <div className="mt-6 space-y-3">
      {automaticReversal ? (
        <div className="rounded-sm border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          Active Used parts will return to Hold only after successful Submit. Usage history is preserved.
        </div>
      ) : null}
      {error ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-destructive/30 bg-destructive/5 px-3 py-2">
          <span className="text-xs text-destructive">{error}</span>
          <Button
            type="button"
            onClick={loadOptions}
            disabled={disabled || loading}
            variant="retry"
            size="sm"
            data-action-category="retry-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Retry
          </Button>
        </div>
      ) : null}
      {repairAction === "part_replace" ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <TransferPanel
            title="HOLD PART"
            rows={holdRows}
            selectedIds={selectedHoldIds}
            onToggle={manualEnabled ? (id) => toggleSelection(setSelectedHoldIds, id) : null}
            actionLabel="Add"
            onAction={() => moveSelected("to_used")}
            actionDisabled={!manualEnabled || selectedHoldIds.length === 0}
            stateMessage={stateMessage}
          />
          <UnifiedUsedPartPanel
            inventoryRows={usedRows}
            inventoryRowId={standardMovePreviewId}
            editMode={manualEnabled}
            selectedInventoryIds={selectedUsedIds}
            onToggleInventory={manualEnabled ? (id) => toggleSelection(setSelectedUsedIds, id) : null}
            onRemoveInventory={() => moveSelected("to_hold")}
            inventorySelectable={isSelectable}
            cannibalLedger={cannibalLedger}
            stateMessage={stateMessage}
          />
        </div>
      ) : null}
      {fieldError || (repairAction === "part_replace" && validationError) ? (
        <p className="text-xs text-destructive">
          {fieldError || validationError}
        </p>
      ) : null}
      {!automaticReversal && repairAction !== "part_replace" ? (
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          Hold/Used editing is available only when Repair Action is Part Replace.
        </p>
      ) : null}
      {loading && loaded ? (
        <p className="text-xs text-zinc-500 dark:text-zinc-400">Refreshing spare-part options...</p>
      ) : null}
      <span className="sr-only">
        {holdsToConvert.length} Holds becoming Used; {usagesToReturn.length} Used returning to Hold.
      </span>
    </div>
  );
}
